/// <amd-module name="ReportsCommon/DonutChartVisual/Components/DonutChartVisual" />
import { VisualTemplate } from "Analytics/Shared/Visuals/Common/Components/VisualTemplate";
import * as React from "react";
import { DonutChartLegendComponent, IDonutChartLegend, IDonutChartLegendProps } from "ReportsCommon/DonutChartVisual/Components/DonutChartLegend";
import { Chart } from "VSS/Features/Charts/Chart";
import { ChartTypesConstants, Datum } from "VSS/Features/Charts/Contracts";
import { css, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IDonutChartVisualDatum extends Datum {
    /** Custom text for Legend, to accomodate existing use scenario. Defaults to Datum.Name. */
    legendLabel?: string;
    legendDisplayValue?: string;
}

export interface IDonutChartVisualLegendOptions {
    className?: string;
    diameter?: number;
}

export interface IDonutChartVisualProps extends IVssComponentProperties {
    title?: string;
    titleClassName?: string;
    titleId?: string;
    ariaLabelledBy?: string;

    subTitle?: string;
    errorText?: string;
    chartDiameter?: number;

    contentRenderer?: (text: string) => JSX.Element;

    scalarLabel?: string;
    scalarContent?: string | number;

    auxiliaryScalarLabel?: string;
    auxiliaryScalarContent?: string | number;

    chartTitle?: string;
    chartData?: IDonutChartVisualDatum[];

    legendOptions?: IDonutChartVisualLegendOptions;
}

interface IScalarContentProps {
    scalarContent?: string | number;
    scalarLabel?: string;
}

const ScalarContent: React.SFC<IScalarContentProps> = props => {
    return (
        <div className="donut-chart-visual-scalar flex-grow flex-column flex-center">
            <div className="donut-chart-visual-scalar-content">
                <div className="donut-chart-visual-scalar-value fontSizeLL fontWeightSemiBold">{props.scalarContent}</div>

                <div className="donut-chart-visual-scalar-label fontSizeMM text-ellipsis">{props.scalarLabel}</div>
            </div>
        </div>
    );
};

/**
 * Responsible for presentation of a common visual pattern:
 * -Title
 * -Scalar quantity on left side
 * -Pie chart in middle
 * -Legend on right side
 */
export class DonutChartVisual extends VssComponent<IDonutChartVisualProps, {}> {
    public static defaultProps: Partial<IDonutChartVisualProps> = {
        chartDiameter: 120
    };

    private static _getXLables(chartProps: IDonutChartVisualProps) {
        return chartProps.chartData ? chartProps.chartData.filter(o => o.y !== 0).map(chartData => chartData.legendLabel || chartData.name || "") : [];
    }

    /**
     * Converts  component props to ChartOptions
     */
    public static formatChartOptions(chartProps: IDonutChartVisualProps): any {
        return {
            title: chartProps.chartTitle,
            hostOptions: chartProps.chartDiameter
                ? {
                    width: chartProps.chartDiameter,
                    height: chartProps.chartDiameter
                }
                : null,
            legend: { enabled: false },
            chartType: ChartTypesConstants.Pie,
            pageContext: chartProps.pageContext,
            suppressMargin: true,
            suppressAnimation: true,
            specializedOptions: {
                size: "120%",
                innerSize: "60%",
                showLabels: false
            },
            series: [{ data: chartProps && chartProps.chartData ? chartProps.chartData.filter(o => o.y !== 0) : [] }],
            xAxis: { labelValues: DonutChartVisual._getXLables(chartProps) }
        };
    }

    public render(): JSX.Element {
        return (
            <VisualTemplate
                className={this.props.className}
                title={this.props.title}
                titleClassName={this.props.titleClassName}
                subTitle={!this.props.errorText ? this.props.subTitle : ""}
                tabIndex={0}
                titleId={this.props.titleId}
                arialabelledby={this.props.ariaLabelledBy}
            >
                {this._getElementToDisplay()}
            </VisualTemplate>
        );
    }

    private _loadingComponent(): JSX.Element {
        return <Spinner className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
    }

    private _getElementToDisplay(): JSX.Element {
        if (this.props.errorText && this.props.contentRenderer) {
            return this.props.contentRenderer(this.props.errorText);
        }

        if (this.props.chartData) {
            return (
                <div className="donut-chart-visual relative">
                    <div className="donut-chart-visual-body flex flex-center">
                        {this.props.scalarContent && <ScalarContent scalarContent={this.props.scalarContent} scalarLabel={this.props.scalarLabel} />}

                        {this.props.auxiliaryScalarContent && <div className="donut-chart-visual-separator" />}

                        {this.props.auxiliaryScalarContent && (
                            <ScalarContent scalarContent={this.props.auxiliaryScalarContent} scalarLabel={this.props.auxiliaryScalarLabel} />
                        )}

                        <div className="donut-chart-visual-component flex-column">
                            <Chart {...this._getDonutChartOptions()} className="flex-column" />
                        </div>

                        <DonutChartLegendComponent {...this._getLegendProps()} />
                    </div>
                </div>
            );
        } else {
            return this._loadingComponent();
        }
    }

    private _getLegendProps(): IDonutChartLegendProps {

        const legendOptions = !!this.props.legendOptions ? this.props.legendOptions : {};
        const className = css("donut-chart-visual-legend flex", legendOptions.className ? legendOptions.className : "");
        const legendDiameter = !!legendOptions.diameter ? legendOptions.diameter : undefined;
        const legendRows = this._getLegendRows();

        return {
            className: className,
            diameter: legendDiameter,
            legend: legendRows
        }
    }

    private _getDonutChartOptions(): any {
        return DonutChartVisual.formatChartOptions(this.props);
    }

    private _getLegendRows(): IDonutChartLegend[] {
        return this.props.chartData
            ? this.props.chartData
                .filter(o => o.y !== 0)
                .map((o, i) => {
                    return {
                        color: o.color,
                        count: o.legendDisplayValue ? o.legendDisplayValue : o.y,
                        label: o.legendLabel ? o.legendLabel : o.name
                    } as IDonutChartLegend;
                })
            : [];
    }
}
